import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  Download, 
  Copy, 
  X, 
  FileText, 
  Calendar,
  Target,
  Brain,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Trash2
} from 'lucide-react';

const ResumeViewer = ({ resumeId, user, onClose, onDelete }) => {
  const [resume, setResume] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedJobDescription, setEditedJobDescription] = useState('');
  const [editedTargetIndustry, setEditedTargetIndustry] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('txt');

  useEffect(() => {
    if (resumeId) {
      loadResume();
    }
  }, [resumeId]);

  const loadResume = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}`);
      
      if (!response.ok) {
        throw new Error('Failed to load resume');
      }
      
      const data = await response.json();
      setResume(data);
      setEditedContent(data.optimized_resume || '');
      setEditedJobDescription(data.job_description || '');
      setEditedTargetIndustry(data.target_industry || '');
    } catch (error) {
      console.error('Failed to load resume:', error);
      setError('Failed to load resume');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          optimized_resume: editedContent,
          job_description: editedJobDescription,
          target_industry: editedTargetIndustry
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save resume');
      }

      const result = await response.json();
      
      // Update local resume state
      setResume(prev => ({
        ...prev,
        optimized_resume: editedContent,
        job_description: editedJobDescription,
        target_industry: editedTargetIndustry,
        updated_at: result.updated_at
      }));

      setIsEditing(false);
      
      // Show success message
      setTimeout(() => {
        alert('Resume saved successfully!');
      }, 100);

    } catch (error) {
      console.error('Failed to save resume:', error);
      setError('Failed to save resume. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format = downloadFormat) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}/download?format=${format}`
      );

      if (!response.ok) {
        throw new Error('Failed to download resume');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `resume_${resumeId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Download failed:', error);
      setError('Download failed. Please try again.');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resume.optimized_resume).then(() => {
      alert('Resume copied to clipboard!');
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this resume? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}?user_id=${user.uid}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete resume');
      }

      alert('Resume deleted successfully!');
      onDelete && onDelete(resumeId);
      onClose();

    } catch (error) {
      console.error('Delete failed:', error);
      setError('Failed to delete resume. Please try again.');
    }
  };

  const cancelEdit = () => {
    setEditedContent(resume.optimized_resume || '');
    setEditedJobDescription(resume.job_description || '');
    setEditedTargetIndustry(resume.target_industry || '');
    setIsEditing(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading resume...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Resume Not Found</h3>
            <p className="text-slate-600 mb-4">The resume could not be loaded.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-8 h-8 mr-3" />
                <div>
                  <h2 className="text-2xl font-bold">Resume Optimization</h2>
                  <p className="text-blue-100">
                    ATS Score: {resume.ats_score}% • 
                    Created: {new Date(resume.created_at).toLocaleDateString()}
                    {resume.updated_at && (
                      <> • Updated: {new Date(resume.updated_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                {!isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Resume
                    </button>
                    <button
                      onClick={handleCopy}
                      className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center space-x-3">
                {/* Download Options */}
                <div className="flex items-center space-x-2">
                  <select
                    value={downloadFormat}
                    onChange={(e) => setDownloadFormat(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  >
                    <option value="txt">📄 Text (.txt)</option>
                    <option value="pdf">📋 PDF (.pdf)</option>
                    <option value="docx">📝 Word (.docx)</option>
                  </select>
                  <button
                    onClick={() => handleDownload()}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                </div>

                {/* Delete Button */}
                <button
                  onClick={handleDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-6">
                {/* Metadata Editing */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Job Description (Optional)
                    </label>
                    <textarea
                      value={editedJobDescription}
                      onChange={(e) => setEditedJobDescription(e.target.value)}
                      className="w-full h-24 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Job description for targeting..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Target Industry (Optional)
                    </label>
                    <input
                      type="text"
                      value={editedTargetIndustry}
                      onChange={(e) => setEditedTargetIndustry(e.target.value)}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., Technology, Healthcare"
                    />
                  </div>
                </div>

                {/* Resume Content Editing */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Resume Content
                  </label>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-96 p-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Resume content..."
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Characters: {editedContent.length} • Words: {editedContent.split(/\s+/).filter(word => word.length > 0).length}
                  </p>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div className="grid lg:grid-cols-4 gap-8">
                {/* Resume Content */}
                <div className="lg:col-span-3">
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Optimized Resume</h3>
                    <div className="max-h-96 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                        {resume.optimized_resume}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Stats and Info */}
                <div className="space-y-6">
                  {/* ATS Score */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-center">
                      <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="text-3xl font-bold text-green-600">{resume.ats_score}%</div>
                      <div className="text-sm text-slate-600">ATS Score</div>
                      {resume.original_score && (
                        <div className="text-xs text-green-600 mt-1">
                          ↑ Improved from {resume.original_score}%
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Provider */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center">
                      <Brain className="w-5 h-5 text-blue-600 mr-2" />
                      <div>
                        <div className="font-medium text-slate-900">AI Provider</div>
                        <div className="text-sm text-slate-600">{resume.ai_provider || 'Unknown'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Processing Time */}
                  {resume.processing_time && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-purple-600 mr-2" />
                        <div>
                          <div className="font-medium text-slate-900">Processing Time</div>
                          <div className="text-sm text-slate-600">{resume.processing_time.toFixed(2)}s</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {resume.keyword_matches && resume.keyword_matches.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Matched Keywords
                      </h4>
                      <div className="space-y-1">
                        {resume.keyword_matches.slice(0, 5).map((keyword, index) => (
                          <div key={index} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {keyword}
                          </div>
                        ))}
                        {resume.keyword_matches.length > 5 && (
                          <div className="text-xs text-slate-500">
                            +{resume.keyword_matches.length - 5} more keywords
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Improvements */}
                  {resume.improvements && resume.improvements.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <h4 className="font-medium text-slate-900 mb-3">Key Improvements</h4>
                      <div className="space-y-2">
                        {resume.improvements.slice(0, 3).map((improvement, index) => (
                          <div key={index} className="text-xs text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" />
                            <span>{improvement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResumeViewer;